import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectsPage.module.css';

// ACADEMIC & ENGINEERING PROJECTS — Synchronized with Jolok Banarjee's updated CV
const ACADEMIC_PROJECTS = [
  {
    title: "A Novel Integrated ML Framework for Turbofan Engine Fault Detection, Condition Monitoring, and RUL Prediction",
    slug: "e2e-pdm-framework",
    role: "Undergraduate Thesis & Research Assistant",
    supervisor: "Air Cdre Dr. Saifur Rahman Bakaul (AAUB)",
    when: "Jan 2025 – Present",
    summary: "Trained and evaluated multiple ML and deep learning models (Isolation Forest, Bi-LSTM, LSTM) on public turbofan datasets to catch engine faults early, track engine health over time, and estimate remaining useful life (RUL). Deployed the results into FastAPI and Streamlit web applications for live fleet monitoring. Manuscript under review at Journal of Aerospace Information Systems, AIAA.",
    tags: ["Machine Learning", "Deep Learning", "Predictive Maintenance", "SHAP", "FastAPI", "Streamlit"],
    image: "assets/project/Thesis 'AI‑Driven Predictive Maintenance for Aircraft Engines'/cover.png",
    driveLink: "https://drive.google.com/drive/folders/1mVmEtwN2LYyY60RzyA6ojFNX9ShFwVV7?usp=sharing",
    badge: "🏆 Best Thesis Award 2025, AAUB",
    badgeColor: "rgba(250, 204, 21, 0.15)",
    badgeText: "#d97706",
  },
  {
    title: "Sustainable Additive Manufacturing Using Recycled PET (rPET)",
    slug: "rpet-additive-manufacturing",
    role: "Project Leader",
    supervisor: "Shahrukh Khan (Assistant Professor, Dept. of Aerospace Eng, AAUB)",
    when: "Jul 2025 – Mar 2026",
    summary: "Led the development of an Arduino-controlled extrusion system for producing 3D printing filament from recycled polyethylene terephthalate (rPET). Conducted tensile testing following ASTM D638 on 45 specimens across nine parameter combinations and analyzed datasets using machine learning (XGBoost, RMSE 0.901 MPa) to optimize mechanical performance. Manuscript under review in Rapid Prototyping Journal.",
    tags: ["Additive Manufacturing", "Recycled PET (rPET)", "ASTM D638", "XGBoost", "Arduino"],
    image: "assets/project/Mechanical Properties of 3D Printed Recycled Plastics (rPET)/cover.jpg",
    driveLink: "https://drive.google.com/drive/folders/1c7wcGOTioPNHaW7UcmYIVkYumXapgWqm?usp=sharing",
    badge: "🥈 Tie 2nd Place — AIAA Region VII Student Conference 2025",
    badgeColor: "rgba(16, 185, 129, 0.15)",
    badgeText: "#047857",
  },
  {
    title: "SkyBee: Homeland Defense Interceptor UCAV",
    slug: "skybee-homeland-defense-interceptor",
    role: "Project Manager — AIAA Undergraduate Team Aircraft Design Competition",
    supervisor: "Faculty Advisor: Md. Toufiq Islam Noor | Project Advisor: Air Cdre Dr. Saifur Rahman Bakaul",
    when: "Oct 2024 – May 2025",
    summary: "Led a 10-person team, supported by seven volunteers, in the conceptual design of 'SkyBee,' a Mach 1.6 unmanned combat aerial vehicle (UCAV) for defensive counter-air patrol, point defense intercept, and escort missions. Coordinated parallel workstreams across configuration, mass properties, structures, and stability & control, delivering a 100-page detailed design report specifying a maximum takeoff weight of 30,984 lb and an endurance of 6.5 hours.",
    tags: ["Aircraft Design", "Aerodynamics", "UCAV", "Structures & FEA", "Stability & Control", "AIAA Competition"],
    image: "assets/project/SkyBee Homeland Defense Interceptor/cover.png",
    driveLink: "https://drive.google.com/drive/folders/1W-H8mFDg9vPdptUZZc9zDBYZa2RoH653?usp=sharing",
    badge: "🥉 3rd Place — AIAA Student Conference VII, 2025 (Team Category)",
    badgeColor: "rgba(139, 92, 246, 0.15)",
    badgeText: "#7c3aed",
  },
  {
    title: "Sky Sentinel S26: Next-Generation Air Defense Interceptor",
    slug: "sky-sentinel-s26",
    role: "Individual Aircraft Design Capstone",
    supervisor: "Air Cdre Dr. Saifur Rahman Bakaul (AAUB)",
    when: "Feb 2025 – Jul 2025",
    summary: "Conducted iterative conceptual design, aerodynamic stability analysis, and performance sizing for a supersonic air-defense interceptor using MATLAB. Achieved Mach 2.2 dash speed with a 750 nm combat radius, 254 m/s initial climb rate, and 22°/sec turn rate. Recognized with the Best Aerospace Vehicle Design Award 2025 at AAUB.",
    tags: ["Aircraft Design", "Aerodynamics", "MATLAB", "Supersonic Flight", "Systems Engineering"],
    image: "/assets/project/Sky Sentinel\u00a0S26 Next‑Generation Air‑Defense Interceptor/cover.png",
    driveLink: "https://drive.google.com/drive/folders/1W-H8mFDg9vPdptUZZc9zDBYZa2RoH653?usp=sharing",
    badge: "🏆 Best Aerospace Vehicle Design Award 2025, AAUB",
    badgeColor: "rgba(250, 204, 21, 0.15)",
    badgeText: "#d97706",
  },
  {
    title: "Bangla Voice-Operated UAV",
    slug: "voice-controlled-drone",
    role: "Team Leader",
    supervisor: "Ishtiaq Ibn Salam (Lecturer, Dept. of Aerospace Eng, AAUB)",
    when: "Mar 2023 – May 2024",
    summary: "Led a 10-person team developing a Bangla voice-command interface for UAV operations, integrating Google Cloud Speech-to-Text with MAVLink through MAVProxy. Evaluated the system through more than 50 outdoor flight tests under varying acoustic and environmental conditions, reporting 95% command recognition accuracy. Resulted in Patent No. 1007044, issued on 25 March 2026.",
    tags: ["UAV Systems", "Bangla Voice Control", "MAVLink", "MAVProxy", "ArduPilot", "Patent"],
    image: "assets/project/First Bangla Voice Command Drone/cover.png",
    driveLink: "https://drive.google.com/drive/folders/1ZZDYp7wrqsO6eXFLCqYSS_Y35mK8Wah2?usp=sharing",
    newsLink: "https://www.youtube.com/watch?v=R5X6N3wK-Fs",
    badge: "💡 Patent No. 1007044 (Issued 25 Mar 2026)",
    badgeColor: "rgba(59, 130, 246, 0.15)",
    badgeText: "#2563eb",
  },
  {
    title: "Picosatellite Software & Automated Weather Satellite Ground Station",
    slug: "weather-satellite-ground-station",
    role: "Team Member — Picosatellite Software & Ground Station",
    supervisor: "Prof. Nazmul Ula (LMU, USA) & Asst. Prof. Md. Samin Rahman (AAUB)",
    when: "Mar 2022 – Feb 2023",
    summary: "Designed an automated AWS-based ground station to receive, process, and visualise NOAA weather satellite data under the joint supervision of Prof. Nazmul Ula (Loyola Marymount University, USA) and Asst. Prof. Md. Samin Rahman (AAUB). Built automated Raspberry Pi scheduling and Amazon S3 cloud archiving pipelines.",
    tags: ["AWS (S3, EC2)", "Satellite Communications", "Ground Station", "Python", "Raspberry Pi"],
    image: "assets/project/Automated Weather Satellite Ground Station/cover.png",
    driveLink: "https://drive.google.com/drive/folders/1OPA5LQq4GJ1-lS0_rqGTBYAXmvoT07Y4?usp=sharing",
    badge: "📡 AAUB & Loyola Marymount University (LMU), USA",
    badgeColor: "rgba(14, 165, 233, 0.15)",
    badgeText: "#0284c7",
  },
  {
    title: "UAS-Based Logistics Network for Critical Medical Delivery",
    slug: "medical-delivery-uav",
    role: "Team Leader",
    supervisor: "Dr. Md. Sakir Hossain (Asst. Prof, Dept. of Avionics Eng, AAUB)",
    when: "Jun 2024 – Sep 2024",
    summary: "Completed preliminary and detailed design phases for a 5kg-payload, 100km-range UAV for critical medical delivery in remote regions. Led payload analysis, mission requirement mapping, and manufacturing budget preparation. Proposal accepted by Research Innovation Center (RIC) under World Bank initiative, securing BDT 55 Lakh in funding.",
    tags: ["UAV Design", "Systems Engineering", "Logistics", "Payload Analysis", "Grant Writing"],
    image: "assets/project/UAS Based Medical Drone/cover.png",
    driveLink: "https://drive.google.com/drive/folders/1sIRiFbtFj6tI9vRo6kvc5gY18JUt9tgF?usp=sharing",
    badge: "💰 BDT 55 Lakh — RIC World Bank Funding",
    badgeColor: "rgba(139, 92, 246, 0.15)",
    badgeText: "#7c3aed",
  }
];

export default function ProjectsPage() {
  const navigate = useNavigate();

  return (
    <div className={styles.container}>

      {/* ── PROJECT GRID ── */}
      <div className={styles.grid}>
        {ACADEMIC_PROJECTS.map((p, idx) => (
          <article
            key={idx}
            className={styles.card}
            onClick={() => navigate(`/projects/${p.slug}`)}
          >
            {/* Image Banner */}
            <div className={styles.imageWrapper}>
              {p.image ? (
                <img src={p.image} alt={p.title} className={styles.image} loading="lazy" />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--muted)',
                  }}
                >
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    No Image Available
                  </span>
                </div>
              )}
            </div>

            {/* Content Area */}
            <div className={styles.content}>
              {/* Badge */}
              {p.badge && (
                <div
                  className={styles.badge}
                  style={{
                    background: p.badgeColor || 'var(--muted)',
                    color: p.badgeText || 'var(--accent)',
                    borderColor: p.badgeText || 'var(--accent)',
                  }}
                >
                  {p.badge}
                </div>
              )}

              <h2 className={styles.title}>{p.title}</h2>

              {/* Metadata Table */}
              <div className={styles.metadataTable}>
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Role:</span>
                  <span className={styles.metaValue}>{p.role}</span>
                </div>
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Supervisor:</span>
                  <span className={styles.metaValue}>{p.supervisor}</span>
                </div>
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Timeline:</span>
                  <span className={styles.metaValue}>{p.when}</span>
                </div>
              </div>

              {/* Summary */}
              <p className={styles.summary}>{p.summary}</p>

              {/* Tags */}
              <div className={styles.tags}>
                {p.tags.map((tag) => (
                  <span key={tag} className={styles.tag}>
                    {tag}
                  </span>
                ))}
              </div>

              {/* Footer Actions */}
              <div className={styles.footer}>
                <button className={styles.readMore}>View Project Details →</button>
                <div className={styles.links}>
                  {p.driveLink && (
                    <a
                      href={p.driveLink}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.iconLink}
                      onClick={(e) => e.stopPropagation()}
                      title="View Project Files"
                    >
                      📄
                    </a>
                  )}
                  {p.newsLink && (
                    <a
                      href={p.newsLink}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.iconLink}
                      onClick={(e) => e.stopPropagation()}
                      title="View Media"
                    >
                      📺
                    </a>
                  )}
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}